import type { DataExplorerDetailFieldProps } from "./types";
import { cn } from "./utils";

function hasContent(value: unknown) {
  return (
    value !== null && value !== undefined && value !== false && value !== ""
  );
}

export function DataExplorerDetailField<TContext = unknown>({
  field,
  label = field?.label,
  icon = field?.icon,
  value = field?.value,
  helperText = field?.helperText,
  error = field?.error,
  required = field?.required ?? false,
  disabled = field?.disabled ?? false,
  children,
  context,
  className,
  valueClassName = field?.valueClassName,
  classNames,
  dataAttributes = field?.dataAttributes,
  labelDataAttributes = field?.labelDataAttributes,
  valueDataAttributes = field?.valueDataAttributes,
  helperDataAttributes = field?.helperDataAttributes,
  errorDataAttributes = field?.errorDataAttributes,
}: DataExplorerDetailFieldProps<TContext>) {
  const resolvedClassNames = {
    ...field?.classNames,
    ...classNames,
  };
  const content =
    children ?? (field?.render ? field.render(context as TContext) : value);
  const hasError = hasContent(error);

  return (
    <div
      {...dataAttributes}
      title={field?.title}
      aria-disabled={disabled || undefined}
      data-disabled={disabled || undefined}
      data-invalid={hasError || undefined}
      data-required={required || undefined}
      className={cn(
        "min-w-0 space-y-1",
        disabled && "opacity-60",
        resolvedClassNames.root,
        field?.className,
        className,
      )}
    >
      <div
        className={cn(
          "flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-muted-foreground",
          resolvedClassNames.label,
        )}
      >
        {icon ? (
          <span
            className={cn(
              "inline-flex shrink-0 items-center justify-center",
              resolvedClassNames.icon,
            )}
          >
            {icon}
          </span>
        ) : null}
        <span {...labelDataAttributes} className="min-w-0 truncate">{label}</span>
        {required ? (
          <span
            aria-hidden="true"
            className={cn("text-destructive-text", resolvedClassNames.required)}
          >
            *
          </span>
        ) : null}
      </div>
      <div
        {...valueDataAttributes}
        className={cn(
          "min-w-0 max-w-full text-[15px] leading-6 text-foreground [overflow-wrap:anywhere]",
          resolvedClassNames.value,
          valueClassName,
        )}
      >
        {content}
      </div>
      {hasContent(helperText) ? (
        <div
          {...helperDataAttributes}
          data-slot="helper"
          className={cn(
            "min-w-0 max-w-full text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere]",
            resolvedClassNames.helper,
          )}
        >
          {helperText}
        </div>
      ) : null}
      {hasError ? (
        <div
          {...errorDataAttributes}
          role="alert"
          data-slot="error"
          className={cn(
            "min-w-0 max-w-full text-xs leading-5 text-destructive-text [overflow-wrap:anywhere]",
            resolvedClassNames.error,
          )}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}
